import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../../services/api';
import socketService from '../../services/socket';
import { useAuth } from '../../contexts/AuthContext';
import '../../components/Chat/Chat.css';
import './Admin.css';

function AdminChat() {
    const { user } = useAuth();
    const [conversations, setConversations] = useState([]);
    const [selectedConv, setSelectedConv] = useState(null);
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [loading, setLoading] = useState(true);
    const [loadingMessages, setLoadingMessages] = useState(false);
    const [sending, setSending] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [isTyping, setIsTyping] = useState(false);
    const [typingUser, setTypingUser] = useState(null);
    const [notificationSound, setNotificationSound] = useState(true);
    const messagesEndRef = useRef(null);
    const fileInputRef = useRef(null);
    const typingTimeoutRef = useRef(null);

    // Scroll to bottom
    const scrollToBottom = useCallback(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, []);

    // Fetch conversations
    const fetchConversations = useCallback(async () => {
        try {
            const response = await api.get('/chat/admin/conversations');
            setConversations(response.data);
        } catch (err) {
            console.error('Error fetching conversations:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    // Fetch chat settings
    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const response = await api.get('/chat/settings');
                setNotificationSound(response.data.notification_sound !== false);
            } catch (err) {
                console.error('Error fetching chat settings:', err);
            }
        };
        fetchSettings();
    }, []);

    useEffect(() => {
        fetchConversations();
        // socket events อัปเดตรายการสนทนาแบบ real-time อยู่แล้ว — poll เป็น fallback (เดิม 30 วิ → 2 นาที)
        const interval = setInterval(fetchConversations, 120000);
        return () => clearInterval(interval);
    }, [fetchConversations]);

    // Socket connection
    useEffect(() => {
        const token = localStorage.getItem('token');
        if (token && user) {
            socketService.connect(token);

            socketService.on('new_message', handleNewMessage);
            socketService.on('user_typing', handleTyping);
            socketService.on('messages_read', handleMessagesRead);
            socketService.on('new_conversation', handleNewConversation);

            return () => {
                socketService.off('new_message', handleNewMessage);
                socketService.off('user_typing', handleTyping);
                socketService.off('messages_read', handleMessagesRead);
                socketService.off('new_conversation', handleNewConversation);
            };
        }
    }, [user]);

    const handleNewMessage = useCallback((message) => {
        // Update messages if it's the current conversation
        if (selectedConv && message.conversation_id === selectedConv.id) {
            setMessages(prev => [...prev, message]);
            setTimeout(scrollToBottom, 100);

            // Mark as read
            api.post('/chat/messages/read', { conversationId: selectedConv.id });
        }

        // Update conversation list
        setConversations(prev => {
            const updated = [...prev];
            const index = updated.findIndex(c => c.id === message.conversation_id);
            if (index > -1) {
                updated[index] = {
                    ...updated[index],
                    last_message: message.content,
                    last_message_at: message.created_at,
                    unread_count: message.conversation_id === selectedConv?.id
                        ? 0
                        : (updated[index].unread_count || 0) + 1
                };
                // Move to top
                const [item] = updated.splice(index, 1);
                updated.unshift(item);
            }
            return updated;
        });

        // Play sound for new messages not in current view (if enabled)
        if ((!selectedConv || message.conversation_id !== selectedConv.id) && notificationSound) {
            playNotificationSound();
        }
    }, [selectedConv, scrollToBottom, notificationSound]);

    const handleTyping = useCallback(({ conversationId, userId, isTyping: typing }) => {
        if (selectedConv?.id === conversationId && userId !== user?.user_id) {
            setIsTyping(typing);
            if (typing) {
                const conv = conversations.find(c => c.id === conversationId);
                setTypingUser(conv?.user_name || 'ผู้ใช้');
            }
        }
    }, [selectedConv, user, conversations]);

    const handleMessagesRead = useCallback(({ conversationId }) => {
        if (selectedConv?.id === conversationId) {
            setMessages(prev => prev.map(msg => ({ ...msg, is_read: true })));
        }
    }, [selectedConv]);

    const handleNewConversation = useCallback((conversation) => {
        setConversations(prev => [conversation, ...prev]);
        playNotificationSound();
    }, []);

    const playNotificationSound = () => {
        try {
            const audio = new Audio('/notification.mp3');
            audio.volume = 0.5;
            audio.play().catch(() => {});
        } catch (e) {}
    };

    // Select conversation
    const selectConversation = async (conv) => {
        setSelectedConv(conv);
        setLoadingMessages(true);
        setMessages([]);

        // Leave previous room
        if (selectedConv?.id) {
            socketService.leaveConversation(selectedConv.id);
        }

        try {
            // Claim conversation if not already admin's
            if (!conv.admin_id) {
                await api.post(`/chat/admin/claim/${conv.id}`);
            }

            // Join room
            socketService.joinConversation(conv.id);

            // Fetch messages
            const response = await api.get(`/chat/messages/${conv.id}`);
            setMessages(response.data);

            // Mark as read
            await api.post('/chat/messages/read', { conversationId: conv.id });

            // Update unread count in list
            setConversations(prev =>
                prev.map(c => c.id === conv.id ? { ...c, unread_count: 0 } : c)
            );
        } catch (err) {
            console.error('Error loading conversation:', err);
        } finally {
            setLoadingMessages(false);
            setTimeout(scrollToBottom, 100);
        }
    };

    // Send message
    const sendMessage = async (e) => {
        e.preventDefault();
        if (!newMessage.trim() || sending || !selectedConv) return;

        setSending(true);
        try {
            const response = await api.post('/chat/messages', {
                conversationId: selectedConv.id,
                content: newMessage.trim(),
                messageType: 'text'
            });

            setMessages(prev => [...prev, response.data.message]);
            setNewMessage('');
            setTimeout(scrollToBottom, 100);

            // Update conversation list
            setConversations(prev =>
                prev.map(c => c.id === selectedConv.id
                    ? { ...c, last_message: newMessage.trim(), last_message_at: new Date().toISOString() }
                    : c
                )
            );
        } catch (err) {
            console.error('Error sending message:', err);
            alert('ไม่สามารถส่งข้อความได้');
        } finally {
            setSending(false);
        }
    };

    // Handle file upload
    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file || !selectedConv) return;

        if (file.size > 10 * 1024 * 1024) {
            alert('ไฟล์ต้องมีขนาดไม่เกิน 10MB');
            return;
        }

        setUploading(true);
        const formData = new FormData();
        formData.append('file', file);
        formData.append('conversationId', selectedConv.id);

        try {
            const response = await api.post('/chat/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            setMessages(prev => [...prev, response.data.message]);
            setTimeout(scrollToBottom, 100);
        } catch (err) {
            console.error('Error uploading file:', err);
            alert('ไม่สามารถอัพโหลดไฟล์ได้');
        } finally {
            setUploading(false);
            fileInputRef.current.value = '';
        }
    };

    // Handle typing indicator
    const handleTypingInput = () => {
        if (selectedConv?.id) {
            socketService.sendTyping(selectedConv.id, true);

            if (typingTimeoutRef.current) {
                clearTimeout(typingTimeoutRef.current);
            }

            typingTimeoutRef.current = setTimeout(() => {
                socketService.sendTyping(selectedConv.id, false);
            }, 2000);
        }
    };

    // Format time
    const formatTime = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const now = new Date();
        const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

        if (diffDays === 0) {
            return date.toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' });
        } else if (diffDays === 1) {
            return 'เมื่อวาน';
        } else if (diffDays < 7) {
            return date.toLocaleDateString('th-TH', { weekday: 'short' });
        } else {
            return date.toLocaleDateString('th-TH', { day: 'numeric', month: 'short' });
        }
    };

    // Get initials
    const getInitials = (name) => {
        if (!name) return '?';
        return name.charAt(0).toUpperCase();
    };

    // Render message content
    const renderMessageContent = (msg) => {
        switch (msg.message_type) {
            case 'image':
                return (
                    <img
                        src={msg.file_url?.startsWith('/uploads') ? `/api${msg.file_url}` : msg.file_url}
                        alt="รูปภาพ"
                        className="chat-image"
                        onClick={() => window.open(msg.file_url?.startsWith('/uploads') ? `/api${msg.file_url}` : msg.file_url, '_blank')}
                    />
                );
            case 'file':
                return (
                    <a
                        href={msg.file_url?.startsWith('/uploads') ? `/api${msg.file_url}` : msg.file_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="chat-file"
                    >
                        <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm-1 2l5 5h-5V4zM6 20V4h5v7h7v9H6z"/>
                        </svg>
                        <span>{msg.file_name || 'ดาวน์โหลดไฟล์'}</span>
                    </a>
                );
            default:
                const urlRegex = /(https?:\/\/[^\s]+)/g;
                const parts = msg.content.split(urlRegex);
                return (
                    <p>
                        {parts.map((part, i) =>
                            urlRegex.test(part) ? (
                                <a key={i} href={part} target="_blank" rel="noopener noreferrer">
                                    {part}
                                </a>
                            ) : part
                        )}
                    </p>
                );
        }
    };

    return (
        <div className="admin-page">
            <div className="admin-header">
                <h1>ระบบแชท</h1>
                <p>สนทนากับผู้ใช้งาน</p>
            </div>

            <div className="admin-chat-page">
                {/* Sidebar - Conversation List */}
                <div className="chat-sidebar">
                    <div className="chat-sidebar-header">
                        <h2>การสนทนา</h2>
                        <span>{conversations.length} รายการ</span>
                    </div>

                    <div className="conversation-list">
                        {loading ? (
                            <div className="chat-loading">
                                <div className="spinner"></div>
                            </div>
                        ) : conversations.length === 0 ? (
                            <div className="no-conversations">
                                <svg viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z"/>
                                </svg>
                                <p>ยังไม่มีการสนทนา</p>
                            </div>
                        ) : (
                            conversations.map(conv => (
                                <div
                                    key={conv.id}
                                    className={`conversation-item ${selectedConv?.id === conv.id ? 'active' : ''}`}
                                    onClick={() => selectConversation(conv)}
                                >
                                    <div className="conversation-avatar">
                                        {getInitials(conv.user_name)}
                                    </div>
                                    <div className="conversation-info">
                                        <div className="conversation-name">{conv.user_name}</div>
                                        <div className="conversation-preview">
                                            {conv.last_message || 'เริ่มการสนทนาใหม่'}
                                        </div>
                                    </div>
                                    <div className="conversation-meta">
                                        <div className="conversation-time">
                                            {formatTime(conv.last_message_at)}
                                        </div>
                                        {conv.unread_count > 0 && (
                                            <span className="conversation-badge">
                                                {conv.unread_count}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>

                {/* Main Chat Area */}
                <div className="chat-main">
                    {!selectedConv ? (
                        <div className="chat-no-selection">
                            <svg viewBox="0 0 24 24" fill="currentColor">
                                <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z"/>
                            </svg>
                            <p>เลือกการสนทนาเพื่อเริ่มแชท</p>
                        </div>
                    ) : (
                        <>
                            {/* Header */}
                            <div className="chat-main-header">
                                <div className="chat-avatar">
                                    <svg viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"/>
                                    </svg>
                                </div>
                                <div className="chat-main-header-info">
                                    <h3>{selectedConv.user_name}</h3>
                                    <span>
                                        {isTyping ? 'กำลังพิมพ์...' : `รหัส: ${selectedConv.user_citizen_id || '-'}`}
                                    </span>
                                </div>
                            </div>

                            {/* Messages */}
                            <div className="chat-main-messages">
                                {loadingMessages ? (
                                    <div className="chat-loading">
                                        <div className="spinner"></div>
                                    </div>
                                ) : messages.length === 0 ? (
                                    <div className="chat-empty">
                                        <svg viewBox="0 0 24 24" fill="currentColor">
                                            <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z"/>
                                        </svg>
                                        <p>ยังไม่มีข้อความ</p>
                                    </div>
                                ) : (
                                    <>
                                        {messages.map((msg, index) => (
                                            <div
                                                key={msg.id || index}
                                                className={`chat-message ${msg.sender_type === 'admin' ? 'sent' : 'received'}`}
                                            >
                                                {msg.sender_type !== 'admin' && (
                                                    <div className="chat-avatar small">
                                                        <svg viewBox="0 0 24 24" fill="currentColor">
                                                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"/>
                                                        </svg>
                                                    </div>
                                                )}
                                                <div className="message-bubble">
                                                    {renderMessageContent(msg)}
                                                    <span className="message-time">
                                                        {formatTime(msg.created_at)}
                                                        {msg.sender_type === 'admin' && (
                                                            <span className={`read-status ${msg.is_read ? 'read' : ''}`}>
                                                                {msg.is_read ? '✓✓' : '✓'}
                                                            </span>
                                                        )}
                                                    </span>
                                                </div>
                                            </div>
                                        ))}
                                        {isTyping && (
                                            <div className="chat-message received">
                                                <div className="chat-avatar small">
                                                    <svg viewBox="0 0 24 24" fill="currentColor">
                                                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"/>
                                                    </svg>
                                                </div>
                                                <div className="message-bubble typing">
                                                    <span></span>
                                                    <span></span>
                                                    <span></span>
                                                </div>
                                            </div>
                                        )}
                                        <div ref={messagesEndRef} />
                                    </>
                                )}
                            </div>

                            {/* Input */}
                            <form className="admin-chat-input" onSubmit={sendMessage}>
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    onChange={handleFileUpload}
                                    accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
                                    style={{ display: 'none' }}
                                />
                                <button
                                    type="button"
                                    className="chat-attach-btn"
                                    onClick={() => fileInputRef.current?.click()}
                                    disabled={uploading}
                                >
                                    {uploading ? (
                                        <div className="spinner small"></div>
                                    ) : (
                                        <svg viewBox="0 0 24 24" fill="currentColor">
                                            <path d="M16.5 6v11.5c0 2.21-1.79 4-4 4s-4-1.79-4-4V5a2.5 2.5 0 0 1 5 0v10.5c0 .55-.45 1-1 1s-1-.45-1-1V6H10v9.5a2.5 2.5 0 0 0 5 0V5c0-2.21-1.79-4-4-4S7 2.79 7 5v12.5c0 3.04 2.46 5.5 5.5 5.5s5.5-2.46 5.5-5.5V6h-1.5z"/>
                                        </svg>
                                    )}
                                </button>
                                <input
                                    type="text"
                                    value={newMessage}
                                    onChange={(e) => setNewMessage(e.target.value)}
                                    onKeyUp={handleTypingInput}
                                    placeholder="พิมพ์ข้อความ..."
                                    disabled={sending || uploading}
                                />
                                <button
                                    type="submit"
                                    className="chat-send-btn"
                                    disabled={!newMessage.trim() || sending}
                                >
                                    {sending ? (
                                        <div className="spinner small"></div>
                                    ) : (
                                        <svg viewBox="0 0 24 24" fill="currentColor">
                                            <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
                                        </svg>
                                    )}
                                </button>
                            </form>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

export default AdminChat;
